/**
 * #1177 / #1181 / #1211 (spec §3.1–§3.3) — the one place a boat update payload
 * is built, shared by the canvas (save, autosave, finalize) and the boat detail
 * page.
 *
 * The payload is the difference between the form and the last server snapshot:
 * values the server already holds — AI-applied ones included — are never sent,
 * an explicit clear is sent as null, relation rows are addressed by id with only
 * their changed keys, a removed row is named in `*_removed_ids`, and a section
 * the dealer never changed is not sent at all. Pure: no React, no I/O.
 */

export type ScalarValue = string | number | boolean | null;

export const ENGINE_KEYS = [
  'engine_manufacturer', 'engine_model', 'engine_horsepower', 'engine_year',
  'engine_hours', 'engine_serial_number', 'drive_type', 'fuel_type',
] as const;
export const TRAILER_KEYS = [
  'trailer_vin', 'trailer_make', 'trailer_model', 'trailer_axles', 'trailer_gvwr', 'shipping_weight',
] as const;
export const EQUIPMENT_KEYS = ['item', 'description', 'price', 'cost', 'sku'] as const;
export const EQUIPMENT_LISTS = ['standard_equipment', 'optional_equipment'] as const;

export type EquipmentList = (typeof EQUIPMENT_LISTS)[number];
export type RowSection = 'engines' | 'trailers' | EquipmentList;

/** One engine, trailer or equipment row; `id` is absent for a row the server has not seen. */
export interface RelationRow {
  id?: string;
  values: Record<string, ScalarValue>;
}

/** The editable state of a boat, normalized so a form and a server boat compare equal. */
export interface EditableBoat {
  version: number;
  scalars: Record<string, ScalarValue>;
  engines: RelationRow[];
  trailers: RelationRow[];
  standard_equipment: RelationRow[];
  optional_equipment: RelationRow[];
  /** Replace-all sections: name → the payload keys sent whole when anything in it changed. */
  groups: Record<string, Record<string, unknown>>;
}

export type PayloadRow = Record<string, ScalarValue>;

export interface SectionChanges {
  rows: PayloadRow[];
  /** The form row each payload row came from (payload index → form index). */
  formIndex: number[];
  removedIds: string[];
}

export interface BoatChanges {
  scalars: Record<string, ScalarValue>;
  engines: SectionChanges;
  trailers: SectionChanges;
  standard_equipment: SectionChanges;
  optional_equipment: SectionChanges;
  groups: Record<string, Record<string, unknown>>;
}

/**
 * A value as the server would store it: strings trimmed, and a blank string, undefined
 * and a non-finite number are no value (Laravel's TrimStrings + ConvertEmptyStringsToNull;
 * S4-39). Otherwise a trailing space is a change forever, and a saved new row never
 * matches the server's copy.
 */
export function normalizeValue(value: unknown): ScalarValue {
  if (value === undefined || value === null) return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed === '' ? null : trimmed;
  }
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'boolean') return value;
  return null;
}

function isNumericString(value: string): boolean {
  return value.trim() !== '' && Number.isFinite(Number(value));
}

/**
 * Equality after normalization. A number equals its numeric string (the API
 * returns decimals as strings, the form holds numbers); two strings compare
 * exactly (a HIN "0012" is not "12").
 */
export function sameValue(a: unknown, b: unknown): boolean {
  const x = normalizeValue(a);
  const y = normalizeValue(b);
  if (x === y) return true;
  if (typeof x === 'number' && typeof y === 'string' && isNumericString(y)) return x === Number(y);
  if (typeof y === 'number' && typeof x === 'string' && isNumericString(x)) return y === Number(x);
  return false;
}

/** JSON with sorted object keys and no undefined members, for comparing whole groups. */
export function stableStringify(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  if (value !== null && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== undefined)
      .sort(([a], [b]) => a.localeCompare(b));
    return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${stableStringify(v)}`).join(',')}}`;
  }
  return JSON.stringify(value ?? null);
}

function idsOf(rows: RelationRow[]): string[] {
  return rows.flatMap((row) => (row.id ? [row.id] : []));
}

function changedValues(before: RelationRow, after: RelationRow, keys: readonly string[]): PayloadRow {
  const out: PayloadRow = {};
  for (const key of keys) {
    if (!sameValue(after.values[key], before.values[key])) out[key] = normalizeValue(after.values[key]);
  }
  return out;
}

function presentValues(row: RelationRow, keys: readonly string[]): PayloadRow {
  const out: PayloadRow = {};
  for (const key of keys) {
    const value = normalizeValue(row.values[key]);
    if (value !== null) out[key] = value;
  }
  return out;
}

function diffSection(baseRows: RelationRow[], currentRows: RelationRow[], keys: readonly string[], orderMatters: boolean): SectionChanges {
  const baseById = new Map(baseRows.flatMap((row) => (row.id ? [[row.id, row] as const] : [])));
  const currentIds = new Set(idsOf(currentRows));
  const survivingBase = idsOf(baseRows).filter((id) => currentIds.has(id));
  const survivingCurrent = idsOf(currentRows).filter((id) => baseById.has(id));
  const reordered = orderMatters && survivingBase.join('|') !== survivingCurrent.join('|');

  const rows: PayloadRow[] = [];
  const formIndex: number[] = [];
  currentRows.forEach((row, index) => {
    const before = row.id ? baseById.get(row.id) : undefined;
    if (!before) {
      const values = presentValues(row, keys);
      if (Object.keys(values).length === 0) return; // an empty "Add" row
      rows.push({ ...(row.id ? { id: row.id } : {}), ...values });
      formIndex.push(index);
      return;
    }
    const changed = changedValues(before, row, keys);
    if (reordered) changed.sort_order = index;
    if (Object.keys(changed).length > 0) {
      rows.push({ id: row.id as string, ...changed });
      formIndex.push(index);
    }
  });

  return { rows, formIndex, removedIds: idsOf(baseRows).filter((id) => !currentIds.has(id)) };
}

/** Both equipment lists together, so a row moved between them is sent by id, not removed and re-added. */
function diffEquipment(base: EditableBoat, current: EditableBoat): Record<EquipmentList, SectionChanges> {
  const baseById = new Map<string, { list: EquipmentList; row: RelationRow }>();
  for (const list of EQUIPMENT_LISTS) {
    for (const row of base[list]) if (row.id) baseById.set(row.id, { list, row });
  }
  const currentIds = new Set(EQUIPMENT_LISTS.flatMap((list) => idsOf(current[list])));

  const out = {} as Record<EquipmentList, SectionChanges>;
  for (const list of EQUIPMENT_LISTS) {
    const stayedBase = idsOf(base[list]).filter((id) => current[list].some((row) => row.id === id));
    const stayedCurrent = idsOf(current[list]).filter((id) => baseById.get(id)?.list === list);
    const reordered = stayedBase.join('|') !== stayedCurrent.join('|');

    const rows: PayloadRow[] = [];
    const formIndex: number[] = [];
    current[list].forEach((row, index) => {
      const before = row.id ? baseById.get(row.id) : undefined;
      if (!before) {
        const values = presentValues(row, EQUIPMENT_KEYS);
        if (Object.keys(values).length === 0) return;
        rows.push({ ...(row.id ? { id: row.id } : {}), ...values, sort_order: index });
        formIndex.push(index);
        return;
      }
      const changed = changedValues(before.row, row, EQUIPMENT_KEYS);
      if (before.list !== list || reordered) changed.sort_order = index;
      if (Object.keys(changed).length > 0) {
        rows.push({ id: row.id as string, ...changed });
        formIndex.push(index);
      }
    });

    out[list] = { rows, formIndex, removedIds: idsOf(base[list]).filter((id) => !currentIds.has(id)) };
  }
  return out;
}

const COST_INPUTS = ['invoice_amount', 'rigging', 'freight'] as const;

export function diffBoat(base: EditableBoat, current: EditableBoat): BoatChanges {
  const scalars: Record<string, ScalarValue> = {};
  for (const key of Object.keys(current.scalars)) {
    if (!sameValue(current.scalars[key], base.scalars[key])) scalars[key] = normalizeValue(current.scalars[key]);
  }

  // total_cost is derived from the three cost inputs (both forms always did this).
  if (COST_INPUTS.some((key) => key in scalars)) {
    const inputs = COST_INPUTS.map((key) => normalizeValue(current.scalars[key]));
    scalars.total_cost = inputs.every((value) => value === null)
      ? null
      : inputs.reduce<number>((sum, value) => sum + (Number(value) || 0), 0);
  }

  const groups: Record<string, Record<string, unknown>> = {};
  for (const [name, value] of Object.entries(current.groups)) {
    if (stableStringify(value) !== stableStringify(base.groups[name])) groups[name] = value;
  }

  const equipment = diffEquipment(base, current);

  return {
    scalars,
    engines: diffSection(base.engines, current.engines, ENGINE_KEYS, true),
    trailers: diffSection(base.trailers, current.trailers, TRAILER_KEYS, false),
    standard_equipment: equipment.standard_equipment,
    optional_equipment: equipment.optional_equipment,
    groups,
  };
}

const SECTIONS: readonly RowSection[] = ['engines', 'trailers', 'standard_equipment', 'optional_equipment'];

export function buildUpdatePayload(
  changes: BoatChanges,
  version: number,
  extra: Record<string, ScalarValue> = {},
): { payload: Record<string, unknown>; pathMap: Record<string, string>; isEmpty: boolean } {
  const payload: Record<string, unknown> = { version, ...changes.scalars, ...extra };
  const pathMap: Record<string, string> = {};
  let hasRelations = false;

  for (const section of SECTIONS) {
    const { rows, formIndex, removedIds } = changes[section];
    if (rows.length > 0) {
      payload[section] = rows;
      rows.forEach((_, index) => {
        pathMap[`${section}.${index}`] = `${section}.${formIndex[index]}`;
      });
      hasRelations = true;
    }
    if (removedIds.length > 0) {
      payload[`${section}_removed_ids`] = removedIds;
      hasRelations = true;
    }
  }

  for (const group of Object.values(changes.groups)) Object.assign(payload, group);

  const isEmpty = Object.keys(changes.scalars).length === 0
    && Object.keys(extra).length === 0
    && !hasRelations
    && Object.keys(changes.groups).length === 0;

  // A no-op mapping (payload index === form index) is not worth reporting.
  for (const [from, to] of Object.entries(pathMap)) if (from === to) delete pathMap[from];

  return { payload, pathMap, isEmpty };
}

/** 422 error paths name payload rows; map them back to the form rows they came from. */
export function mapErrorPaths(errors: Record<string, string[]>, pathMap: Record<string, string>): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const [path, messages] of Object.entries(errors)) {
    const match = /^(engines|trailers|standard_equipment|optional_equipment)\.(\d+)(\..*)?$/.exec(path);
    const mapped = match
      ? `${pathMap[`${match[1]}.${match[2]}`] ?? `${match[1]}.${match[2]}`}${match[3] ?? ''}`
      : path;
    out[mapped] = messages;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Rebase (spec §3.3 "Client rebase", §2 hydration): re-apply my pending changes
// onto a newer server state. For every field I changed since `base`, my value
// wins; otherwise the server's does. A field changed differently on both sides
// is a conflict (mine is kept until the dealer chooses).
// ---------------------------------------------------------------------------

export interface FieldConflict {
  /** A scalar key, `engines.<id>.<key>`, `trailers.<id>.<key>`, `equipment.<id>.<key|list>` or `group:<name>`. */
  path: string;
  mine: unknown;
  theirs: unknown;
}

/** A row I edited that was removed elsewhere — reported, never silently dropped. */
export interface LostRow {
  section: RowSection;
  id: string;
  values: Record<string, ScalarValue>;
}

export interface RebaseResult {
  merged: EditableBoat;
  conflicts: FieldConflict[];
  lostRows: LostRow[];
}

function byId(rows: RelationRow[]): Map<string, RelationRow> {
  return new Map(rows.flatMap((row) => (row.id ? [[row.id, row] as const] : [])));
}

function mergeRow(
  pathPrefix: string,
  before: RelationRow,
  mineRow: RelationRow,
  theirRow: RelationRow,
  keys: readonly string[],
  conflicts: FieldConflict[],
): RelationRow {
  const values = { ...theirRow.values };
  for (const key of keys) {
    if (sameValue(mineRow.values[key], before.values[key])) continue;
    values[key] = normalizeValue(mineRow.values[key]);
    if (!sameValue(theirRow.values[key], before.values[key]) && !sameValue(mineRow.values[key], theirRow.values[key])) {
      conflicts.push({
        path: `${pathPrefix}.${theirRow.id}.${key}`,
        mine: normalizeValue(mineRow.values[key]),
        theirs: normalizeValue(theirRow.values[key]),
      });
    }
  }
  return { id: theirRow.id, values };
}

function rebaseSection(
  section: 'engines' | 'trailers',
  baseRows: RelationRow[],
  mineRows: RelationRow[],
  theirRows: RelationRow[],
  keys: readonly string[],
  conflicts: FieldConflict[],
  lostRows: LostRow[],
): RelationRow[] {
  const baseById = byId(baseRows);
  const mineById = byId(mineRows);
  const theirIds = new Set(idsOf(theirRows));
  const merged: RelationRow[] = [];

  for (const theirRow of theirRows) {
    const before = theirRow.id ? baseById.get(theirRow.id) : undefined;
    const mineRow = theirRow.id ? mineById.get(theirRow.id) : undefined;
    if (before && !mineRow) continue; // I removed it: it stays removed
    if (!before || !mineRow) {
      merged.push(theirRow); // added elsewhere
      continue;
    }
    merged.push(mergeRow(section, before, mineRow, theirRow, keys, conflicts));
  }

  for (const mineRow of mineRows) {
    if (!mineRow.id) {
      merged.push(mineRow); // I added it
      continue;
    }
    if (theirIds.has(mineRow.id)) continue;
    const before = baseById.get(mineRow.id);
    if (before && keys.some((key) => !sameValue(mineRow.values[key], before.values[key]))) {
      lostRows.push({ section, id: mineRow.id, values: mineRow.values });
    }
  }

  return merged;
}

function rebaseEquipment(
  base: EditableBoat,
  mine: EditableBoat,
  theirs: EditableBoat,
  conflicts: FieldConflict[],
  lostRows: LostRow[],
): Record<EquipmentList, RelationRow[]> {
  const locate = (boat: EditableBoat) => {
    const map = new Map<string, { list: EquipmentList; row: RelationRow }>();
    for (const list of EQUIPMENT_LISTS) for (const row of boat[list]) if (row.id) map.set(row.id, { list, row });
    return map;
  };
  const baseById = locate(base);
  const mineById = locate(mine);
  const theirsById = locate(theirs);
  const out: Record<EquipmentList, RelationRow[]> = { standard_equipment: [], optional_equipment: [] };

  for (const list of EQUIPMENT_LISTS) {
    for (const theirRow of theirs[list]) {
      const before = theirRow.id ? baseById.get(theirRow.id) : undefined;
      const mineEntry = theirRow.id ? mineById.get(theirRow.id) : undefined;
      if (before && !mineEntry) continue;
      if (!before || !mineEntry) {
        out[list].push(theirRow);
        continue;
      }
      const row = mergeRow('equipment', before.row, mineEntry.row, theirRow, EQUIPMENT_KEYS, conflicts);
      const movedHere = mineEntry.list !== before.list;
      if (movedHere && list !== before.list && mineEntry.list !== list) {
        conflicts.push({ path: `equipment.${theirRow.id}.list`, mine: mineEntry.list, theirs: list });
      }
      out[movedHere ? mineEntry.list : list].push(row);
    }
  }

  for (const list of EQUIPMENT_LISTS) {
    for (const mineRow of mine[list]) {
      if (!mineRow.id) {
        out[list].push(mineRow);
        continue;
      }
      if (theirsById.has(mineRow.id)) continue;
      const before = baseById.get(mineRow.id);
      if (before && (before.list !== list || EQUIPMENT_KEYS.some((key) => !sameValue(mineRow.values[key], before.row.values[key])))) {
        lostRows.push({ section: list, id: mineRow.id, values: mineRow.values });
      }
    }
  }

  return out;
}

export function rebase(base: EditableBoat, mine: EditableBoat, theirs: EditableBoat): RebaseResult {
  const conflicts: FieldConflict[] = [];
  const lostRows: LostRow[] = [];

  const scalars: Record<string, ScalarValue> = { ...theirs.scalars };
  for (const key of Object.keys(mine.scalars)) {
    if (sameValue(mine.scalars[key], base.scalars[key])) continue;
    scalars[key] = normalizeValue(mine.scalars[key]);
    if (!sameValue(theirs.scalars[key], base.scalars[key]) && !sameValue(mine.scalars[key], theirs.scalars[key])) {
      conflicts.push({ path: key, mine: normalizeValue(mine.scalars[key]), theirs: normalizeValue(theirs.scalars[key]) });
    }
  }

  const groups: Record<string, Record<string, unknown>> = { ...theirs.groups };
  for (const name of Object.keys(mine.groups)) {
    const mineValue = stableStringify(mine.groups[name]);
    const baseValue = stableStringify(base.groups[name]);
    if (mineValue === baseValue) continue;
    groups[name] = mine.groups[name];
    const theirValue = stableStringify(theirs.groups[name]);
    if (theirValue !== baseValue && mineValue !== theirValue) {
      conflicts.push({ path: `group:${name}`, mine: mine.groups[name], theirs: theirs.groups[name] ?? null });
    }
  }

  const equipment = rebaseEquipment(base, mine, theirs, conflicts, lostRows);

  return {
    merged: {
      version: theirs.version,
      scalars,
      engines: rebaseSection('engines', base.engines, mine.engines, theirs.engines, ENGINE_KEYS, conflicts, lostRows),
      trailers: rebaseSection('trailers', base.trailers, mine.trailers, theirs.trailers, TRAILER_KEYS, conflicts, lostRows),
      standard_equipment: equipment.standard_equipment,
      optional_equipment: equipment.optional_equipment,
      groups,
    },
    conflicts,
    lostRows,
  };
}

export type ConflictChoice = 'mine' | 'theirs';

/** Apply the dealer's per-field choices to a rebased state. Unchosen paths keep mine. */
export function applyConflictChoices(
  merged: EditableBoat,
  conflicts: FieldConflict[],
  choices: Record<string, ConflictChoice>,
): EditableBoat {
  const next = structuredClone(merged);

  for (const conflict of conflicts) {
    if ((choices[conflict.path] ?? 'mine') === 'mine') continue;
    const { path, theirs } = conflict;

    if (path.startsWith('group:')) {
      next.groups[path.slice('group:'.length)] = (theirs ?? {}) as Record<string, unknown>;
      continue;
    }

    const [section, id, key] = path.split('.');
    if (id === undefined || key === undefined) {
      next.scalars[path] = normalizeValue(theirs);
      continue;
    }

    if (section === 'equipment') {
      const list = EQUIPMENT_LISTS.find((name) => next[name].some((row) => row.id === id));
      if (!list) continue;
      const row = next[list].find((candidate) => candidate.id === id) as RelationRow;
      if (key === 'list' && (theirs === 'standard_equipment' || theirs === 'optional_equipment') && theirs !== list) {
        next[list] = next[list].filter((candidate) => candidate.id !== id);
        next[theirs].push(row);
      } else if (key !== 'list') {
        row.values[key] = normalizeValue(theirs);
      }
      continue;
    }

    if (section === 'engines' || section === 'trailers') {
      const row = next[section].find((candidate) => candidate.id === id);
      if (row) row.values[key] = normalizeValue(theirs);
    }
  }

  return next;
}
